import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import type { AllAcceptedFileTypes } from './types';
import { DEFAULT_MAX_FILES_UPLOAD } from './constants.ts';
import { getFormattedFileTypes, handleFileDrop } from './utils.ts';

type HelperTextProps = {
  multiple: boolean;
  dragActive: boolean;
  isUploading: boolean;
  hideFileTypes?: boolean;
  allowZip: boolean | undefined;
  acceptedFileTypes: AllAcceptedFileTypes;
};

const HelperText = ({
  isUploading,
  acceptedFileTypes,
  dragActive,
  allowZip,
  hideFileTypes = false,
  multiple = false,
}: HelperTextProps) => {
  if (isUploading) {
    return (
      <Typography component="span" variant="body2" sx={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>
        Uploading
      </Typography>
    );
  }

  const fileTypes = getFormattedFileTypes({ types: acceptedFileTypes, allowZip });

  if (dragActive) {
    return (
      <Typography color="primary" component="span" variant="body2">
        Drop here
      </Typography>
    );
  }

  return (
    <Typography color="tertiary" component="p" variant="body2">
      <Typography color="primary" component="span" variant="body2" fontWeight="bold">
        Browse
      </Typography>{' '}
      or drag and drop your{hideFileTypes ? '' : ` ${fileTypes}`} file{multiple ? 's' : ''} here
    </Typography>
  );
};

type UploadBoxProps<TFormValues extends FieldValues> = {
  id: Path<TFormValues>;
  onChange: (files: FileList) => void;
  setError: UseFormSetError<TFormValues>;
  acceptedFileTypes?: AllAcceptedFileTypes;
  allowZip?: boolean;
  maxFiles?: number;
  isUploading?: boolean;
  hideFileTypes?: boolean;
};

export const UploadBox = <TFormValues extends FieldValues>({
  id: _id,
  onChange,
  setError,
  acceptedFileTypes = 'ALL_TYPES',
  allowZip = false,
  maxFiles = DEFAULT_MAX_FILES_UPLOAD,
  isUploading = false,
  hideFileTypes = false,
}: UploadBoxProps<TFormValues>) => {
  const [dragActive, setDragActive] = useState(false);

  const id = `upload-box-${_id.toString()}`;

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDirectoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files) {
      onChange(files);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    setDragActive(false);

    await handleFileDrop({
      event,
      allowZip,
      setError,
      acceptedFileTypes,
      onChange,
      id: _id.toString(),
    });
  };

  const handleClick = () => {
    if (isUploading) {
      return;
    }

    document.getElementById(id)?.click();
  };

  const accept = React.useMemo(() => {
    if (acceptedFileTypes === 'ALL_TYPES') {
      return undefined;
    }

    return [...acceptedFileTypes, allowZip ? 'application/zip' : undefined].filter(Boolean).join(',');
  }, [acceptedFileTypes, allowZip]);

  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        borderRadius: 1,
        borderWidth: 1,
        borderColor: dragActive ? theme.palette.primary.light : theme.palette.primary.main,
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        height: '150px',
        cursor: 'pointer',
        borderStyle: dragActive ? 'solid' : 'dashed',
        boxShadow: dragActive ? `inset 0 0 2px ${theme.palette.primary.light}` : 'none',
        position: 'relative',
        opacity: isUploading ? 0.5 : 1,
        pointerEvents: isUploading ? 'none' : 'auto',
        padding: 1,
        textAlign: 'center',
      })}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseLeave={() => setDragActive(false)}
    >
      <input
        id={id}
        type="file"
        multiple={maxFiles > 1}
        accept={accept}
        onChange={handleDirectoryChange}
        style={{ display: 'none' }}
      />
      <HelperText
        isUploading={isUploading}
        acceptedFileTypes={acceptedFileTypes}
        dragActive={dragActive}
        allowZip={allowZip}
        hideFileTypes={hideFileTypes}
        multiple={maxFiles > 1}
      />
    </Box>
  );
};
